import React, { useEffect, useRef, forwardRef } from 'react';
import { observer } from 'mobx-react';

const defaultStyles = {
	position: 'absolute',
	top: 'calc(100% + 0.5rem)', // 검색창과 약간의 간격
	left: 0,
	right: 0,
	backgroundColor: 'white',
	border: 'none',
	borderRadius: '1rem', // 둥근 모서리
	boxShadow: '0 4px 12px rgba(0,0,0,0.15)', // 더 깊은 그림자
	filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))', // SearchBarWrapper와 비슷한 그림자
	zIndex: 10000,
	maxHeight: '240px',
	overflowY: 'auto',
	// 스크롤바 스타일링
	scrollbarWidth: 'thin',
	scrollbarColor: '#ccc transparent',
	fontFamily: 'NotoSansKR-Regular', // 폰트 통일
};

const defaultItemStyles = {
	base: {
		padding: '12px 20px', // SearchBarWrapper와 동일한 패딩
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		transition: 'background-color 0.15s ease',
		fontFamily: 'NotoSansKR-Regular',
		fontSize: '14px',
	},

	selected: {
		backgroundColor: '#f5f5f5', // 연한 회색 (gray_f5와 비슷)
		borderRadius: '8px',
		margin: '2px 8px',
	},

	normal: {
		backgroundColor: 'transparent',
		borderRadius: '8px',
		margin: '2px 8px',
	},
};

const AutocompleteDropdown = forwardRef(function AutocompleteDropdown(
	{
		items,
		selectedIndex,
		onItemClick,
		onItemHover,
		isVisible,
		renderItem,
		className,
		style,
	},
	ref,
) {
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);

	// ref가 전달되면 dropdownRef에 연결
	useEffect(() => {
		if (ref) {
			if (typeof ref === 'function') {
				ref(dropdownRef.current);
			} else {
				ref.current = dropdownRef.current;
			}
		}
	}, [ref]);

	// 선택된 항목이 변경될 때 스크롤 조정
	useEffect(() => {
		if (selectedItemRef.current && dropdownRef.current && selectedIndex >= 0) {
			const dropdown = dropdownRef.current;
			const selectedItem = selectedItemRef.current;

			// 현재 스크롤 위치와 드롭다운 높이 정보
			const scrollTop = dropdown.scrollTop;
			const dropdownHeight = dropdown.clientHeight;
			const itemHeight = selectedItem.clientHeight;
			const itemTop = selectedItem.offsetTop;

			// 선택된 항목이 드롭다운 위쪽으로 벗어난 경우
			if (itemTop < scrollTop) {
				dropdown.scrollTo({
					top: itemTop,
					behavior: 'smooth',
				});
			}
			// 선택된 항목이 드롭다운 아래쪽으로 벗어난 경우
			else if (itemTop + itemHeight > scrollTop + dropdownHeight) {
				dropdown.scrollTo({
					top: itemTop - dropdownHeight + itemHeight,
					behavior: 'smooth',
				});
			}
		}
	}, [selectedIndex]);

	if (!isVisible || items.length === 0) {
		return null;
	}

	const dropdownStyle = { ...defaultStyles, ...style };

	return (
		<div
			className={`autocomplete-dropdown ${className || ''}`}
			style={dropdownStyle}
			ref={dropdownRef}>
			{items.map((item, index) => {
				const isSelected = selectedIndex === index;

				if (renderItem) {
					return (
						<div
							key={item.id}
							ref={isSelected ? selectedItemRef : null}
							onClick={() => onItemClick(item)}
							onMouseEnter={() => onItemHover(index)}>
							{renderItem(item, index, isSelected)}
						</div>
					);
				}

				return (
					<div
						key={item.id}
						ref={isSelected ? selectedItemRef : null}
						style={{
							...defaultItemStyles.base,
							...(isSelected
								? defaultItemStyles.selected
								: defaultItemStyles.normal),
						}}
						onClick={() => onItemClick(item)}
						onMouseEnter={() => onItemHover(index)}>
						{item.type && (
							<span
								style={{
									padding: '4px 8px',
									fontSize: '11px',
									borderRadius: '12px', // 더 둥근 라벨
									backgroundColor:
										item.type === 'title' ? '#e8f4fd' : '#f3e8ff',
									color: item.type === 'title' ? '#1976d2' : '#7b1fa2',
									fontWeight: '500',
									minWidth: 'fit-content',
								}}>
								{item.type === 'title' ? '제목' : '태그'}
							</span>
						)}
						<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
							<span
								style={{
									fontWeight: item.type === 'tag' ? '500' : 'normal',
									color: '#333',
								}}>
								{item.label}
							</span>
							{item.type === 'tag' && (
								<span
									style={{
										fontSize: '12px',
										color: '#666',
										marginTop: '2px',
									}}>
									이 태그로 검색
								</span>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
});

export default observer(AutocompleteDropdown);
